import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DataGrid, GridCellParams } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Toast } from '@components';
import AgencyDialogForm from './AgencyDialogForm';
import AgenciesTableToolbar from './AgenciesTableToolbar';
import AgenciesFilterBar from './AgenciesFilterBar';
import { AgencyData } from '../../../utils/types'
import { useUser } from '../../../hooks/useUser'
import { useAgency } from '../../../hooks/useAgency'
import { searchAgencies } from '../../../networkRequest/agenciesService'
import { downloadUserListReport } from '../../../networkRequest/officialsService'
import { Pagination } from '../../../networkRequest/api'
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { Logger } from '../../../utils/logger'
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import Utils from '../../../utils/utils';
import { PAGE_PATHS } from '../../../constants';
import { FilterState, buildInitialFilterState, getFilterParams, FILTER_URL_KEYS, FilterKey } from './filterState';

const FETCH_AGENCIES_ERROR = 'Failed to fetch agencies.';
const EXPORT_USER_LIST_ERROR = 'Failed to export the user list. Please check your connection and try again.';
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;

export type AgencyOfficialRolesMap = Map<string, number>;

export const AgenciesTable: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [agencies, setAgencies] = useState<AgencyData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isExportingUserList, setIsExportingUserList] = useState(false);
  const [gridPagination, setGridPagination] = useState(() => ({
    page: Utils.parseIntParam(searchParams, 'page') ?? DEFAULT_PAGE,
    pageSize: Utils.parseIntParam(searchParams, 'page_size') ?? DEFAULT_PAGE_SIZE,
  }));
  const [pagination, setPagination] = useState<Pagination>({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    total_count: 0,
  });
  const [searchText, setSearchText] = useState(() => searchParams.get('search') ?? '');
  const [filterState, setFilterState] = useState<FilterState>(() => {
    const parseSelected = (urlKey: string): Record<string, boolean> =>
      Object.fromEntries(Utils.parseArrayParam(searchParams, urlKey).map(v => [v, true]));

    return Object.fromEntries(
      (Object.entries(FILTER_URL_KEYS) as [FilterKey, string][])
        .map(([key, urlKey]) => [key, parseSelected(urlKey)])
    ) as FilterState;
  });
  const { user } = useUser();
  const { setAgency } = useAgency();

  const fetchParams = useMemo(
    () => ({ searchText, gridPagination, filterState }),
    [searchText, gridPagination, filterState]
  );
  const debouncedParams = useDebouncedValue(fetchParams, 300);
  // Search count ref is used to ignore out-of-order responses
  const searchCountRef = useRef<number>(0);

  const fetchAgencies = useCallback(
    async () => {
      if (!user?.accessToken) return;

      // Increment the search count and save it to the ref
      const newSearchCount = searchCountRef.current + 1;
      searchCountRef.current = newSearchCount;
      const { page, pageSize } = debouncedParams.gridPagination;

      try {
        setLoading(true);

        const { data, pagination, error, errors } = await searchAgencies({
          accessToken: user.accessToken,
          query: debouncedParams.searchText,
          limit: pageSize,
          offset: page * pageSize,
          ...getFilterParams(debouncedParams.filterState),
        });

        // Ignore if response is older than the current search count
        if (newSearchCount < searchCountRef.current) return;

        if (error || errors?.length || !data || !pagination) {
          setErrorMessage(error ?? errors?.join(' ') ?? FETCH_AGENCIES_ERROR);
          return;
        }

        setPagination(pagination);
        setAgencies(data);
      } catch (error) {
        // Log only non-identifying context; search text is free-form user input
        Logger.error(error instanceof Error ? error.message : String(error), {
          page,
          pageSize,
          hasSearch: debouncedParams.searchText.length > 0,
          ...getFilterParams(debouncedParams.filterState),
        });
        if (newSearchCount === searchCountRef.current) {
          setErrorMessage(FETCH_AGENCIES_ERROR);
        }
      } finally {
        if (newSearchCount === searchCountRef.current) {
          setLoading(false);
        }
      }
    },
    [user?.accessToken, debouncedParams]
  );

  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedParams.gridPagination.page !== DEFAULT_PAGE) {
      params.page = String(debouncedParams.gridPagination.page);
    }
    if (debouncedParams.gridPagination.pageSize !== DEFAULT_PAGE_SIZE) {
      params.page_size = String(debouncedParams.gridPagination.pageSize);
    }
    if (debouncedParams.searchText) {
      params.search = debouncedParams.searchText;
    }
    for (const [key, urlKey] of Object.entries(FILTER_URL_KEYS) as [FilterKey, string][]) {
      const values = Object.keys(debouncedParams.filterState[key]);
      if (values.length > 0) {
        params[urlKey] = values.join(',');
      }
    }
    setSearchParams(params, { replace: true });
  }, [debouncedParams, setSearchParams]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setGridPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleFilterChange = useCallback((newState: FilterState) => {
    setFilterState(newState);
    setGridPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleAddAgency = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleExportUserList = useCallback(async () => {
    if (!user?.accessToken) return;
    setIsExportingUserList(true);
    try {
      await downloadUserListReport(user.accessToken);
    } catch (error) {
      Logger.error(error instanceof Error ? error.message : String(error), { action: 'exportUserList' });
      setErrorMessage(EXPORT_USER_LIST_ERROR);
    } finally {
      setIsExportingUserList(false);
    }
  }, [user?.accessToken]);

  // A new agency may not belong on the current page, so reload it from the server
  const handleFormResponse = () => {
    fetchAgencies();
  }

  const columns = [
    { field: 'name', headerName: 'Name', flex: 3, minWidth: 150 },
    { field: 'email_domain', headerName: 'Email Domain', flex: 3, minWidth: 200 },
    { field: 'kind', headerName: 'Kind', flex: 2, minWidth: 120 },
    { field: 'contact_email', headerName: 'Contact Email', flex: 3, minWidth: 200 },
    {
      field: 'agency_operation',
      headerName: 'SurePass Admin Control',
      flex: 3,
      minWidth: 200,
      renderCell: (params: GridCellParams) => (
        <PrimaryButton
          action={(e) => {
            e.stopPropagation();
            setAgency(params.row)
            navigate(PAGE_PATHS.users, {
              state: {
                isAdmin: true
              }
            });
          }}
        >
          Manage this Agency
        </PrimaryButton>
      )
    }
  ];

  return (
    <>
      {errorMessage != '' && (
        <Toast type="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      {successMessage && (
        <Toast type="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      <AgenciesFilterBar
        filterState={filterState}
        onFilterChange={handleFilterChange}
        onClearAll={() => handleFilterChange(buildInitialFilterState())}
      />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          loading={loading}
          data-testid="agencies"
          columns={columns}
          rows={agencies}
          rowCount={pagination.total_count}
          paginationMode="server"
          paginationModel={gridPagination}
          onPaginationModelChange={setGridPagination}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          localeText={{
            toolbarColumns: 'Manage Columns'
          }}
          showToolbar
          slots={{ toolbar: AgenciesTableToolbar }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
            toolbar: {
              searchText,
              onSearchChange: handleSearchChange,
              onAddAgency: handleAddAgency,
              onExportUserList: handleExportUserList,
              isExportingUserList,
            },
          }}
          sx={{
            '& .clickable-cell:hover': {
              cursor: 'pointer'
            },
            '--DataGrid-overlayHeight': '300px',
            height: '100%',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600'
            },
            '& .MuiDataGrid-toolbarContainer': {
              backgroundColor: '#E3F2FD'
            },
            '& .MuiDataGrid-columnHeaderTitle, & .MuiButton-text, & [data-testid="IndeterminateCheckBoxIcon"], & [data-testid="CheckBoxIcon"], & [data-testid="CheckBoxOutlineBlankIcon"]':
            {
              color: 'primary.main'
            },
            '& .MuiDataGrid-overlayWrapperInner': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            },
            // Keep the pagination controls clear of the Help Scout beacon
            '& .MuiDataGrid-footerContainer': {
              marginRight: '80px',
            },
          }}
        />
      </Box>
      <AgencyDialogForm
        open={isDialogOpen}
        setDialogOpen={setIsDialogOpen}
        setSuccessMessage={setSuccessMessage}
        handleResponse={handleFormResponse}
      />
    </>
  );
};

export default AgenciesTable;
